import React, { type FC } from 'react';
import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import Heading from '@atlaskit/heading';
import { Text, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	ContentStatePopupBase,
	ChangeStatusButton,
} from '../ContentStatePopupBase/ContentStatePopupBase.tsx';

const styles = cssMap({
	body: {
		padding: token('space.200'),
	},
});

const i18n = defineMessages({
	heading: {
		id: 'content-state.otherModeReviewBlockingPopup.heading',
		defaultMessage: 'Approval in progress',
		description:
			'Heading shown when user tries to start approval but the other page mode already has one in progress',
	},
	bodyViewMode: {
		id: 'content-state.otherModeReviewBlockingPopup.body-view-mode',
		defaultMessage:
			'This page has a draft in review. Complete or cancel that review to start a new approval for the published page.',
		description: 'Body text shown in published view when the draft has an in-progress review',
	},
	bodyEditMode: {
		id: 'content-state.otherModeReviewBlockingPopup.body-edit-mode',
		defaultMessage:
			'The published version of this page is in review. Complete or cancel that review to start a new approval for this draft.',
		description: 'Body text shown in editor when the published version has an in-progress review',
	},
});

type OtherModeReviewBlockingPopupProps = {
	isViewMode: boolean;
	onChangeStatus: () => void;
};

export const OtherModeReviewBlockingPopup: FC<OtherModeReviewBlockingPopupProps> = ({
	isViewMode,
	onChangeStatus,
}) => {
	const { formatMessage } = useIntl();
	return (
		<ContentStatePopupBase footer={<ChangeStatusButton onClick={onChangeStatus} />}>
			<Stack xcss={styles.body} space="space.100">
				<Heading as="h2" size="xsmall">
					{formatMessage(i18n.heading)}
				</Heading>
				<Text color="color.text.subtle">
					{formatMessage(isViewMode ? i18n.bodyViewMode : i18n.bodyEditMode)}
				</Text>
			</Stack>
		</ContentStatePopupBase>
	);
};
